/**
 * JournalEvidencePanel — Phase 3.
 *
 * Renders the canonical operational proof behind a posted journal entry.
 * Pure presentation; consumes the resolver in `evidenceResolver.ts`.
 */
import { Link } from "react-router-dom";
import {
  User, MapPin, FileText, DollarSign, Wrench, ClipboardCheck,
  ShieldCheck, AlertTriangle, Receipt, Clock, FileSignature, ChevronRight,
} from "lucide-react";
import type { EvidenceItem, EvidenceKind } from "@/data/accounting/evidenceResolver";

const ICONS: Record<EvidenceKind, typeof User> = {
  customer: User,
  contract: FileSignature,
  invoice: FileText,
  payment: DollarSign,
  installer: Wrench,
  wrap: ClipboardCheck,
  qa: ShieldCheck,
  service_checklist: ShieldCheck,
  field_add: Receipt,
  callback: AlertTriangle,
  pay_ledger: Receipt,
  settlement: DollarSign,
  address: MapPin,
  schedule: Clock,
  note: FileText,
};

export function JournalEvidencePanel({ items }: { items: EvidenceItem[] }) {
  if (!items || items.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-muted-foreground bg-muted/20">
        No linked operational evidence for this entry.
      </div>
    );
  }
  return (
    <div className="bg-muted/20 border-t border-border">
      <div className="px-4 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        Operational evidence
      </div>
      <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-2 gap-1.5">
        {items.map((it, i) => {
          const Icon = ICONS[it.kind] ?? FileText;
          const toneClass =
            it.tone === "positive" ? "text-foreground" :
            it.tone === "warn" ? "text-amber-600 dark:text-amber-400" :
            "text-foreground";
          const inner = (
            <div className="flex items-start gap-2 px-2 py-1.5 rounded-sm hover:bg-accent/40 group">
              <Icon className={`h-3.5 w-3.5 mt-0.5 shrink-0 ${toneClass}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-xs">
                  <span className="font-medium">{it.label}</span>
                  {it.timestamp ? (
                    <span className="text-[10px] text-muted-foreground">
                      {fmtTs(it.timestamp)}
                    </span>
                  ) : null}
                </div>
                {it.detail ? (
                  <div className="text-[11px] text-muted-foreground mt-0.5">{it.detail}</div>
                ) : null}
              </div>
              {it.link ? <ChevronRight className="h-3 w-3 text-muted-foreground/60 opacity-0 group-hover:opacity-100 shrink-0 mt-1" /> : null}
            </div>
          );
          return it.link
            ? <Link key={i} to={it.link}>{inner}</Link>
            : <div key={i}>{inner}</div>;
        })}
      </div>
    </div>
  );
}

function fmtTs(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}
